import React from 'react'
import {
    Modal,
    Upload,
    Button,
    Icon,
    message,
    DatePicker,
    Spin,
    Statistic,
    Row,
    Col,
    Alert,
    Table,
    Checkbox,
    Tooltip,
    Progress
} from 'antd'
import Papa from 'papaparse'
import { observer } from 'mobx-react'
import moment from 'moment'
import { parseTokenRows, RawTokenRow, TokenRow } from './Utils/parseCsvRows'
import { validateCsvHeaders } from './Utils/validateCsvHeaders'
import { extractErrorMessage } from './Utils/extractErrorMessage'
import { SKIPPED_REASON_LABELS, emptyResultsDecision } from './Utils/skippedReasons'
import replacedRows from '@services/replacedRows'
import SocketService from '@pages/UserServiceDashboard/Utils/socket'

// ── types ───────────────────────────────────────────────────
type Stage = 'upload' | 'date_select' | 'processing' | 'results' | 'done'
type LogType = 'info' | 'success' | 'warning' | 'error'
// Actions controlled by the row master select checkbox.
type ActionFlag = 'tranzila' | 'block' | 'cancellation'
// Outcome of the post-failure batch-status check (null = normal, no failure handled yet).
// 'done'/'in_progress' block a blind retry; 'retryable' keeps the current retry behavior.
type BatchOutcome = 'in_progress' | 'done' | 'retryable' | null
// Row type returned by the backend. Controls available actions and row styling.
type RowType =
    | 'full'
    | 'has_cancellation'
    | 'blocked_sub'
    | 'other_status'
    | 'no_sub'
    | 'no_user'
    | 'sub_mismatch'
    | 'not_in_tranzila'

interface LogEntry {
    text: string
    type: LogType
    time: string
}

interface ProcessingResults {
    total: number
    saved: number
    missing: number
    willUpdate: number
}

interface UserSubToBlock {
    row_type: RowType
    _row_id: string
    replaced_card_id: number
    user_sub_id: number | null
    user_id: number | null
    user_name: string | null
    sto_id: string | null
    terminal_name: string
    last_4_digits: string | null
    has_cancellation_request: boolean
    existing_token: string
}

interface RowFlags {
    select: boolean
    tranzila: boolean
    block: boolean
    cancellation: boolean
}

interface ActionCounters {
    success: number
    failed: number
}

interface DoneSummary {
    tranzila: ActionCounters
    block: ActionCounters
    cancellation: ActionCounters
}

interface IProps {
    visible: boolean
    onClose: () => void
    // apply-from-listing mode — wired here, consumed in a follow-up change
    applyMode?: boolean
    applyDates?: Array<{ date: string; count: number }>
}

interface IState {
    stage: Stage
    uploadStatus: { icon: string; title: string; color: string }
    data: TokenRow[]
    csvUploadDate: moment.Moment | null
    logs: LogEntry[]
    results: ProcessingResults | null
    loading: boolean
    userSubsToBlock: UserSubToBlock[]
    rowFlags: { [rowId: string]: RowFlags }
    doneSummary: DoneSummary | null
    progress: { processed: number; total: number; stage?: 'fetch' | 'rows' } | null
    batchOutcome: BatchOutcome
    // apply mode only: APPLY_REQUIRE records the backend couldn't refresh (Tranzila down / STO not found)
    applySkipped: Array<{ id: number; four_digits: string | null; reason?: string | null }>
    // apply response `skipped`: every submitted card had already left APPLY_REQUIRE → nothing ran
    applyAllSkipped: boolean
    // apply response `audit_failed`: the actions RAN but process_archive/status was never written
    auditFailed: Array<number | string>
    // apply 503: the run aborted part-way; `processed` excludes the record it aborted on
    applyAbort: { processed: number; total: number; four_digits: string | null } | null
    // rerun `rate_limited`/`unavailable`: the run aborted, so records past that point were never
    // reached — they appear in neither `rows` nor `skipped`. Non-null means the run is incomplete.
    rerunAbort: { reason: 'rate_limited' | 'unavailable'; processed: number | null; total: number | null } | null
}

// ── constants ───────────────────────────────────────────────
const LOG_COLORS: Record<LogType, string> = {
    info: '#d9d9d9',
    success: '#52c41a',
    warning: '#faad14',
    error: '#f5222d'
}

const LOG_PREFIX: Record<LogType, string> = {
    info: '●',
    success: '✓',
    warning: '⚠',
    error: '✗'
}

const ACTION_FLAGS: ActionFlag[] = ['tranzila', 'block', 'cancellation']
// Guard against the wrong file being uploaded, not a throttle: classification does one bulk
// Tranzila call per run, but apply still calls Tranzila once per STO. Must match the backend's cap.
const MAX_ROWS = 500

// Row display order: problematic rows first, rows ready for handling last.
const ROW_TYPE_ORDER: { [key in RowType]: number } = {
    not_in_tranzila: 0,
    no_user: 1,
    no_sub: 2,
    sub_mismatch: 3,
    other_status: 4,
    blocked_sub: 5,
    has_cancellation: 6,
    full: 7
}

const ALL_FALSE_FLAGS: RowFlags = { select: false, tranzila: false, block: false, cancellation: false }

interface ActionAvailability {
    tranzila: boolean
    block: boolean
    cancellation: boolean
}

// Which of the three actions are selectable for the given row — mirrors the backend
// availabilityMatrix (ReplacedCardsApplyState): backend 'available' → selectable checkbox here;
// 'not_required'/'manual_required' → not selectable in this UI.
// Cancellation is a close+create on the backend, so it's available for full/has_cancellation/
// blocked_sub regardless of an existing request (has_cancellation_request is info only, below).
const getActionAvailability = (record: UserSubToBlock): ActionAvailability => {
    const rowType = record.row_type
    switch (rowType) {
        case 'full':
            return { tranzila: true, block: true, cancellation: true }
        case 'has_cancellation':
            return { tranzila: true, block: true, cancellation: true }
        case 'blocked_sub':
            return { tranzila: true, block: false, cancellation: true }
        case 'other_status':
            // sto/tranzila available (the STO exists in Tranzila — freeze is valid); block &
            // cancellation are manual_required on the backend → not selectable here.
            return { tranzila: true, block: false, cancellation: false }
        case 'no_sub':
        case 'no_user':
            return { tranzila: true, block: false, cancellation: false }
        case 'sub_mismatch':
            // The subscription found for this STO is attached to a DIFFERENT sto_id (customer moved
            // to a new charge). Freezing this STO is safe; block & cancellation are manual_required
            // on the backend → never selectable here.
            return { tranzila: true, block: false, cancellation: false }
        case 'not_in_tranzila':
            return { tranzila: false, block: false, cancellation: false }
        default:
            return { tranzila: false, block: false, cancellation: false }
    }
}

// Tooltip text for a disabled checkbox. Empty string means no tooltip.
const getActionTooltip = (record: UserSubToBlock, action: ActionFlag): string => {
    const rowType = record.row_type
    if (rowType === 'has_cancellation' && action === 'cancellation') return 'בקשת ביטול כבר קיימת'
    if (rowType === 'blocked_sub' && action === 'block') return 'בוצעה חסימה במערכת'
    // blocked_sub + cancellation: show a tooltip only when a request already exists and the checkbox is unavailable.
    // If no request exists, the action is available and renders as a normal checkbox without a tooltip.
    if (rowType === 'blocked_sub' && action === 'cancellation')
        return record.has_cancellation_request ? 'בקשת ביטול כבר קיימת' : ''
    if (rowType === 'other_status' && action === 'block') return 'המנוי בוטל במערכת'
    if (rowType === 'no_user' && (action === 'block' || action === 'cancellation')) return 'לא נמצא משתמש'
    if (rowType === 'sub_mismatch' && (action === 'block' || action === 'cancellation'))
        return 'המנוי משויך לחיוב אחר'
    return ''
}

const getInitialState = (): IState => ({
    stage: 'upload',
    uploadStatus: {
        icon: 'upload',
        title: 'לחץ או גרור קובץ לאזור זה להעלאה',
        color: '#ffc069'
    },
    data: [],
    csvUploadDate: moment(),
    logs: [],
    results: null,
    loading: false,
    userSubsToBlock: [],
    rowFlags: {},
    doneSummary: null,
    progress: null,
    batchOutcome: null,
    applySkipped: [],
    applyAllSkipped: false,
    auditFailed: [],
    applyAbort: null,
    rerunAbort: null
})

// ── component ───────────────────────────────────────────────
@observer
class CsvUploader extends React.Component<IProps, IState> {
    state = getInitialState()
    private logEndRef = React.createRef<HTMLDivElement>()
    private _unmounted = false
    private progressHandler: ((msg: any) => void) | null = null
    private progressEvent: string | null = null
    private currentRequestId: number = 0
    // Synchronous in-flight guard. State/loading flags update asynchronously, leaving a
    // double-click gap; this instance boolean is set synchronously to hard-block re-entry.
    private inFlight: boolean = false
    // Generation counter for CSV parses. A newer upload bumps this so an earlier parse's
    // async `complete` callback is ignored (prevents stale data restoring into a closed/reopened modal).
    private uploadSeq: number = 0

    componentDidUpdate(prevProps: IProps, prevState: IState) {
        // Apply mode: opening (visible false→true) skips the upload flow and loads DB rows.
        const justOpened = this.props.visible && !prevProps.visible
        if (justOpened && this.props.applyMode) {
            this.enterApplyMode()
        }
        if (prevState.logs.length !== this.state.logs.length) {
            const el = this.logEndRef.current
            if (el) el.scrollIntoView({ behavior: 'smooth' })
        }
    }

    componentWillUnmount() {
        this._unmounted = true
        this.cleanupProgress()
    }

    cleanupProgress = () => {
        if (this.progressHandler && this.progressEvent) {
            SocketService.unsubscribe(this.progressEvent, this.progressHandler)
        }
        this.progressHandler = null
        this.progressEvent = null
        this.currentRequestId = 0
    }

    // ── helpers ─────────────────────────────────────────────
    addLog = (text: string, type: LogType = 'info') => {
        if (this._unmounted) return
        this.setState(prev => ({
            logs: [...prev.logs, { text, type, time: moment().format('HH:mm:ss') }]
        }))
    }

    safeSetState = (patch: Partial<IState>) => {
        if (!this._unmounted) this.setState(patch as any)
    }

    // Build the results-stage state (sorted rows + per-row action checkboxes) from a
    // user_subs_to_block[] array. Shared by the create-success path and apply mode so the
    // table + apply behave identically regardless of how the rows were obtained.
    buildResultsState = (rows: UserSubToBlock[]) => {
        const userSubsToBlock = rows.slice().sort((a, b) => ROW_TYPE_ORDER[a.row_type] - ROW_TYPE_ORDER[b.row_type])
        const rowFlags: { [rowId: string]: RowFlags } = {}
        userSubsToBlock.forEach(item => {
            const avail = getActionAvailability(item)
            rowFlags[item._row_id] = {
                tranzila: avail.tranzila,
                block: avail.block,
                cancellation: avail.cancellation,
                select: avail.tranzila || avail.block || avail.cancellation
            }
        })
        return { userSubsToBlock, rowFlags }
    }

    // A rerun aborted by Tranzila leaves every record past the abort point untouched AND unmentioned
    // — they appear in neither `rows` nor `skipped`. Only report counts we actually have: the last
    // progress event, falling back to the chosen date's record count for the denominator. Never
    // rows.length, which is the count that survived, not the count that existed.
    buildRerunAbort = (data: any, date: string): IState['rerunAbort'] => {
        const rateLimited = !!(data && data.rate_limited)
        const unavailable = !!(data && data.unavailable)
        if (!rateLimited && !unavailable) return null

        const progress = this.state.progress
        const dateEntry = (this.props.applyDates || []).filter(d => d.date === date)[0]
        return {
            reason: rateLimited ? 'rate_limited' : 'unavailable',
            processed: progress && typeof progress.processed === 'number' ? progress.processed : null,
            total: progress && progress.total > 0 ? progress.total : dateEntry ? dateEntry.count : null
        }
    }

    // ── apply-existing mode: load APPLY_REQUIRE rows from the DB instead of uploading a CSV ──
    enterApplyMode = () => {
        const dates = this.props.applyDates || []
        if (dates.length === 1) {
            this.loadApplyRows(dates[0].date) // straight to results
        } else {
            this.setState({ stage: 'date_select' }) // pick a date first
        }
    }

    loadApplyRows = async (date: string) => {
        // unique id for this run — used to ignore progress events from prior (still-running) requests
        const requestId = Date.now()
        this.currentRequestId = requestId

        this.setState({ stage: 'processing', loading: true, progress: null, applySkipped: [], rerunAbort: null })

        // Try to establish socket + get its id (best-effort — progress is an enhancement)
        let socketId: string | undefined
        try {
            const socket = SocketService.getSocket()
            if (socket.connected) {
                socketId = socket.id
            } else {
                // wait briefly for connect
                socketId = await new Promise<string | undefined>(resolve => {
                    const onConnect = () => resolve(socket.id)
                    socket.once('connect', onConnect)
                    // safety timeout — don't block the request if socket never connects
                    setTimeout(() => {
                        socket.off('connect', onConnect)
                        resolve(undefined)
                    }, 3000)
                })
            }
        } catch (e) {
            socketId = undefined
        }

        // Subscribe to progress only if we have a socket id
        if (socketId) {
            this.progressHandler = (msg: any) => {
                if (this._unmounted) return
                // ignore stale/other-run events; String() both sides — the backend echoes
                // request_id from query/body where it may arrive as a string
                if (!msg || String(msg.request_id) !== String(requestId)) return
                if (typeof msg.processed === 'number') {
                    this.safeSetState({
                        progress: {
                            processed: msg.processed,
                            total: msg.total,
                            stage: msg.stage === 'fetch' ? 'fetch' : 'rows'
                        }
                    })
                }
            }
            this.progressEvent = 'applyRequireProgress'
            SocketService.subscribe('applyRequireProgress', this.progressHandler)
        }

        try {
            const res = await replacedRows.getApplyRequireRows(date, socketId, requestId)
            if (this._unmounted) return
            // ignore stale responses from a superseded run
            if (this.currentRequestId !== requestId) return
            const rows: UserSubToBlock[] = (res.data && res.data.rows) || []
            const skipped = (res.data && res.data.skipped) || []
            const rerunAbort = this.buildRerunAbort(res.data, date)
            const { userSubsToBlock, rowFlags } = this.buildResultsState(rows)
            // results-stage render guards on a non-null `results`; its create-only fields
            // (saved/missing/willUpdate) aren't shown here — the stats are computed from the rows.
            this.safeSetState({
                userSubsToBlock,
                rowFlags,
                applySkipped: skipped,
                rerunAbort,
                results: { total: rows.length, saved: 0, missing: 0, willUpdate: 0 },
                stage: 'results',
                loading: false
            })
            this.cleanupProgress()
        } catch (e) {
            this.cleanupProgress()
            if (this._unmounted) return
            message.error('שגיאה בטעינת נתונים')
            const multi = (this.props.applyDates || []).length > 1
            this.safeSetState({ stage: multi ? 'date_select' : 'upload', loading: false })
        }
    }

    // ── stage 1: upload & parse ─────────────────────────────
    handleUpload = (file: File) => {
        this.setState({
            uploadStatus: { icon: 'loading', title: 'קורא קובץ...', color: '#1890ff' },
            data: [],
            results: null,
            logs: [],
            userSubsToBlock: [],
            rowFlags: {},
            doneSummary: null,
            progress: null,
            batchOutcome: null,
            applySkipped: [],
            applyAllSkipped: false,
            auditFailed: [],
            applyAbort: null,
            rerunAbort: null
        })
        this.addLog('קורא קובץ...')

        // Bump the parse generation and capture it locally; a newer upload (or close/reopen)
        // supersedes this one, and its stale `complete` callback is ignored below.
        this.uploadSeq += 1
        const seq = this.uploadSeq

        Papa.parse<RawTokenRow>(file, {
            header: true,
            skipEmptyLines: true,
            // Trim header whitespace at parse time so validation and parsing agree on key names
            // (e.g. " Update Type " → "Update Type"). Otherwise validateCsvHeaders trims but the
            // raw keys read by parseTokenRows do not, dropping rows.
            transformHeader: (h: string) => h.trim(),
            complete: results => {
                // Ignore a stale parse: a newer upload superseded this one, or the modal unmounted.
                if (this._unmounted || seq !== this.uploadSeq) return

                const validation = validateCsvHeaders(results.meta && results.meta.fields)
                if (!validation.valid) {
                    this.safeSetState({
                        data: [],
                        uploadStatus: {
                            icon: 'close-circle',
                            title: validation.error || 'מבנה קובץ לא תקין',
                            color: '#f5222d'
                        }
                    })
                    this.addLog(validation.error, 'error')
                    message.error(validation.error)
                    return
                }

                const rawRows = results.data
                const { rows, warnings } = parseTokenRows(rawRows)
                if (rows.length > MAX_ROWS) {
                    const errorMessage = `יותר מדי שורות בקובץ (מקסימום ${MAX_ROWS})`
                    this.safeSetState({
                        stage: 'upload',
                        data: [],
                        uploadStatus: {
                            icon: 'close-circle',
                            title: errorMessage,
                            color: '#f5222d'
                        }
                    })
                    this.addLog(errorMessage, 'error')
                    message.error(errorMessage)
                    return
                }

                // Zero kept rows is not a success: nothing can be processed and the start button
                // never appears. The file was read correctly, so it's a warning, not a failure —
                // worded by cause, because rows dropped by the filter (unknown Update Type,
                // blocked+applied) are discarded silently and would otherwise look like a bug.
                const emptyResultTitle =
                    rawRows.length === 0
                        ? 'הקובץ ריק — לא נמצאו שורות. יש לבחור קובץ אחר'
                        : `לא נמצאו שורות מתאימות בקובץ (מתוך ${rawRows.length}) — יש לבחור קובץ אחר`

                this.safeSetState({
                    data: rows,
                    uploadStatus:
                        rows.length === 0
                            ? {
                                  icon: 'exclamation-circle',
                                  title: emptyResultTitle,
                                  color: '#faad14'
                              }
                            : {
                                  icon: 'check-circle',
                                  title: `הקובץ פוענח בהצלחה — ${rows.length} שורות`,
                                  color: '#52c41a'
                              }
                })
                this.addLog(`קובץ נקרא — ${rawRows.length} שורות סה״כ`, 'info')
                this.addLog(`לאחר סינון (blocked/unfrozen) — ${rows.length} שורות`, 'success')
                warnings.forEach(w => this.addLog(w, 'warning'))
            },
            error: error => {
                if (this._unmounted || seq !== this.uploadSeq) return

                this.safeSetState({
                    uploadStatus: { icon: 'close-circle', title: 'שגיאה בקריאת קובץ', color: '#f5222d' }
                })
                this.addLog(`שגיאה: ${error.message}`, 'error')
                message.error('שגיאה בקריאת קובץ')
            }
        })
        return false
    }

    // ── stage 2: send to backend ────────────────────────────
    handleStartProcessing = async () => {
        const { data, csvUploadDate } = this.state
        if (!csvUploadDate) {
            message.warning('יש לבחור תאריך')
            return
        }

        // Local YYYY-MM-DD (no UTC conversion). Used for BOTH the create claim and the
        // getBatchStatus check so they refer to the identical calendar day on UTC+ timezones.
        const dateStr = csvUploadDate.format('YYYY-MM-DD')

        // Hard guard against a fast double-click (set synchronously, before any await).
        if (this.inFlight) return
        this.inFlight = true

        // unique id for this run — used to ignore progress events from prior (still-running) requests
        const requestId = Date.now()
        this.currentRequestId = requestId

        this.setState({ stage: 'processing', loading: true, progress: null, batchOutcome: null })
        this.addLog('שולח נתונים לשרת...')

        // Try to establish socket + get its id (best-effort — progress is an enhancement)
        let socketId: string | undefined
        try {
            const socket = SocketService.getSocket()
            if (socket.connected) {
                socketId = socket.id
            } else {
                // wait briefly for connect
                socketId = await new Promise<string | undefined>(resolve => {
                    const onConnect = () => resolve(socket.id)
                    socket.once('connect', onConnect)
                    // safety timeout — don't block the request if socket never connects
                    setTimeout(() => {
                        socket.off('connect', onConnect)
                        resolve(undefined)
                    }, 3000)
                })
            }
        } catch (e) {
            socketId = undefined
        }

        // Subscribe to progress only if we have a socket id
        if (socketId) {
            this.progressHandler = (msg: any) => {
                if (this._unmounted) return
                // ignore stale/other-run events; String() both sides — the backend echoes
                // request_id from query/body where it may arrive as a string
                if (!msg || String(msg.request_id) !== String(requestId)) return
                if (typeof msg.processed === 'number') {
                    this.safeSetState({
                        progress: {
                            processed: msg.processed,
                            total: msg.total,
                            stage: msg.stage === 'fetch' ? 'fetch' : 'rows'
                        }
                    })
                }
            }
            this.progressEvent = 'replaceProgress'
            SocketService.subscribe('replaceProgress', this.progressHandler)
        }

        try {
            const response = await replacedRows.createReplacedRows({
                date: dateStr,
                rows: data,
                socket_id: socketId,
                request_id: requestId
            })
            // ignore stale responses from a superseded run
            if (this.currentRequestId !== requestId) return

            const results: ProcessingResults = {
                total: data.length,
                saved: response.data.saved,
                missing: response.data.missing,
                willUpdate: response.data.will_update
            }
            const { userSubsToBlock, rowFlags } = this.buildResultsState(response.data.user_subs_to_block || [])

            this.safeSetState({
                results,
                userSubsToBlock,
                rowFlags,
                stage: 'results',
                loading: false
            })
            this.addLog('שמירה הושלמה', 'success')
            this.addLog(
                `סה״כ: ${results.total} | הצלחות: ${results.saved} | חסרים: ${results.missing} | מנויים לחסימה: ${results.willUpdate}`,
                'info'
            )
            this.cleanupProgress()
        } catch (error) {
            const errorMessage = extractErrorMessage(error, 'שגיאה בשמירת נתונים')
            this.safeSetState({ stage: 'upload', loading: false, batchOutcome: null })
            this.addLog(errorMessage, 'error')
            message.error(errorMessage)
            this.cleanupProgress()

            const errorResponse = error && error.response
            const responseData = errorResponse && errorResponse.data
            const responseStatus = errorResponse && errorResponse.status
            const hasApplicationCode =
                responseData && typeof responseData === 'object' && typeof responseData.code === 'string'
            const isGatewayTimeout =
                responseStatus === 502 || responseStatus === 503 || responseStatus === 504
            const shouldCheckBatchStatus = !errorResponse || (isGatewayTimeout && !hasApplicationCode)

            // Only an absent response or an unmarked gateway timeout is ambiguous: the server may
            // have committed after the connection was lost. Application errors are definitive.
            if (!shouldCheckBatchStatus) return

            try {
                const res = await replacedRows.getBatchStatus(dateStr)
                const status = res.data && res.data.status
                if (status === 'done') {
                    this.safeSetState({ batchOutcome: 'done' })
                } else if (status === 'in_progress') {
                    this.safeSetState({ batchOutcome: 'in_progress' })
                } else {
                    // 'failed' or 'none' → truly not committed → allow retry
                    this.safeSetState({ batchOutcome: 'retryable' })
                }
            } catch (e) {
                // status check itself failed → fall back to allowing retry (current behavior)
                this.safeSetState({ batchOutcome: 'retryable' })
            }
        } finally {
            this.inFlight = false
        }
    }

    // ── stage 3: apply actions ──────────────────────────────
    handleApplyChanges = async () => {
        const { userSubsToBlock, rowFlags } = this.state

        // A row participates only when select=true.
        const isRowActive = (item: UserSubToBlock) => {
            const f = rowFlags[item._row_id]
            return !!(f && f.select)
        }

        // An action is counted only when it is allowed for the row type and checked.
        const isActionOn = (item: UserSubToBlock, action: ActionFlag) => {
            const f = rowFlags[item._row_id]
            return !!(f && getActionAvailability(item)[action] && f[action])
        }

        // Check whether at least one action is checked among active rows.
        const hasAnyAction = userSubsToBlock.some(item => {
            if (!isRowActive(item)) return false
            return isActionOn(item, 'tranzila') || isActionOn(item, 'block') || isActionOn(item, 'cancellation')
        })

        if (!hasAnyAction) {
            message.warning('יש לבחור לפחות פעולה אחת')
            return
        }

        // A row enters the payload only when select=true and at least one
        // allowed checked action exists. actions = checked && available.
        const rows = userSubsToBlock
            .filter(item => {
                const f = rowFlags[item._row_id]
                if (!f || !f.select) return false
                const a = getActionAvailability(item)
                return (f.tranzila && a.tranzila) || (f.block && a.block) || (f.cancellation && a.cancellation)
            })
            .map(item => {
                const f = rowFlags[item._row_id]
                const a = getActionAvailability(item)
                return {
                    replaced_card_id: item.replaced_card_id,
                    user_sub_id: item.user_sub_id,
                    user_id: item.user_id,
                    sto_id: item.sto_id,
                    terminal_name: item.terminal_name,
                    last_4_digits: item.last_4_digits,
                    row_type: item.row_type,
                    actions: {
                        tranzila: !!(f.tranzila && a.tranzila),
                        block: !!(f.block && a.block),
                        cancellation: !!(f.cancellation && a.cancellation)
                    }
                }
            })

        // Hard guard against a fast double-click (set synchronously, before any await).
        if (this.inFlight) return
        this.inFlight = true

        // unique id for this run — used to ignore progress events from prior (still-running) requests
        const requestId = Date.now()
        this.currentRequestId = requestId

        this.setState({
            stage: 'processing',
            loading: true,
            progress: null,
            applyAllSkipped: false,
            auditFailed: [],
            applyAbort: null
        })
        this.addLog('מבצע פעולות...')

        // Try to establish socket + get its id (best-effort — progress is an enhancement)
        let socketId: string | undefined
        try {
            const socket = SocketService.getSocket()
            if (socket.connected) {
                socketId = socket.id
            } else {
                // wait briefly for connect
                socketId = await new Promise<string | undefined>(resolve => {
                    const onConnect = () => resolve(socket.id)
                    socket.once('connect', onConnect)
                    // safety timeout — don't block the request if socket never connects
                    setTimeout(() => {
                        socket.off('connect', onConnect)
                        resolve(undefined)
                    }, 3000)
                })
            }
        } catch (e) {
            socketId = undefined
        }

        // Subscribe to progress only if we have a socket id
        if (socketId) {
            this.progressHandler = (msg: any) => {
                if (this._unmounted) return
                // ignore stale/other-run events; String() both sides — the backend echoes
                // request_id from query/body where it may arrive as a string
                if (!msg || String(msg.request_id) !== String(requestId)) return
                if (typeof msg.processed === 'number') {
                    this.safeSetState({ progress: { processed: msg.processed, total: msg.total } })
                }
            }
            this.progressEvent = 'applyProgress'
            SocketService.subscribe('applyProgress', this.progressHandler)
        }

        try {
            const response = await replacedRows.applyActions({
                socket_id: socketId,
                request_id: requestId,
                rows
            })
            // ignore stale responses from a superseded run
            if (this.currentRequestId !== requestId) return

            const safe = (c: any): ActionCounters => ({
                success: (c && c.success) || 0,
                failed: (c && c.failed) || 0
            })
            const doneSummary: DoneSummary = {
                tranzila: safe(response.data.tranzila),
                block: safe(response.data.block),
                cancellation: safe(response.data.cancellation)
            }

            // `skipped` → every card had already left APPLY_REQUIRE, nothing ran.
            // `audit_failed` → the actions DID run but their audit write failed.
            const applyAllSkipped = response.data.skipped === true
            const auditFailed = Array.isArray(response.data.audit_failed) ? response.data.audit_failed : []

            this.safeSetState({
                stage: 'done',
                doneSummary,
                applyAllSkipped,
                auditFailed,
                loading: false
            })
            this.addLog(
                `הפעולות הושלמו — ביטול בטרנזילה: ${doneSummary.tranzila.success}/${doneSummary.tranzila.success +
                    doneSummary.tranzila.failed} | חסימת מנוי: ${doneSummary.block.success}/${doneSummary.block
                    .success + doneSummary.block.failed} | בקשת ביטול: ${doneSummary.cancellation.success}/${doneSummary
                    .cancellation.success + doneSummary.cancellation.failed}`,
                'success'
            )
            this.cleanupProgress()
        } catch (error) {
            // ignore stale errors from a superseded run
            if (this.currentRequestId !== requestId) return

            const errorMessage = extractErrorMessage(error, 'שגיאה בביצוע הפעולות')

            // A mid-run abort reports how far it got; those rows already ran and must not be
            // resubmitted. Both abort kinds carry these fields and are handled identically here —
            // 503 (Tranzila unavailable) and 429 (rate-limit cooldown) — because the operator's
            // next action is the same: wait, then rerun the remaining rows. Detection is on the
            // fields, not the status code. Absent these fields, behave exactly as before.
            const errorData = error && error.response && error.response.data
            const hasAbortInfo =
                !!errorData && typeof errorData.processed === 'number' && typeof errorData.total === 'number'
            const abortedAt = errorData && errorData.aborted_at

            this.safeSetState({
                stage: 'results',
                loading: false,
                applyAbort: hasAbortInfo
                    ? {
                          processed: errorData.processed,
                          total: errorData.total,
                          four_digits: (abortedAt && abortedAt.four_digits) || null
                      }
                    : null
            })
            this.addLog(errorMessage, 'error')
            message.error(errorMessage)
            this.cleanupProgress()
        } finally {
            this.inFlight = false
        }
    }

    // Toggle a single action. If all actions are unchecked, select is unchecked;
    // if at least one action is checked, select is checked.
    handleActionToggle = (rowId: string, flagName: ActionFlag, checked: boolean) => {
        this.setState(prev => {
            const current = prev.rowFlags[rowId]
            const updated = { ...current, [flagName]: checked }
            const anyActionChecked = ACTION_FLAGS.some(a => updated[a])
            updated.select = anyActionChecked
            return {
                rowFlags: { ...prev.rowFlags, [rowId]: updated }
            }
        })
    }

    // Row master toggle: select marks only actions that are allowed for the row.
    handleSelectToggle = (rowId: string, checked: boolean) => {
        const record = this.state.userSubsToBlock.find(r => r._row_id === rowId)
        if (!record) return
        const avail = getActionAvailability(record)
        this.setState(prev => ({
            rowFlags: {
                ...prev.rowFlags,
                [rowId]: {
                    select: checked,
                    tranzila: checked && avail.tranzila,
                    block: checked && avail.block,
                    cancellation: checked && avail.cancellation
                }
            }
        }))
    }

    // Toggle all checkboxes in one action column, only for rows where the action is allowed.
    handleColumnToggle = (flagName: ActionFlag, checked: boolean) => {
        const { userSubsToBlock } = this.state
        this.setState(prev => {
            const newFlags = { ...prev.rowFlags }
            userSubsToBlock.forEach(item => {
                if (!getActionAvailability(item)[flagName]) return
                const updated = { ...newFlags[item._row_id], [flagName]: checked }
                updated.select = ACTION_FLAGS.some(a => updated[a])
                newFlags[item._row_id] = updated
            })
            return { rowFlags: newFlags }
        })
    }

    getColumnHeaderState(flagName: ActionFlag): { checked: boolean; indeterminate: boolean } {
        const { userSubsToBlock, rowFlags } = this.state
        const eligibleRows = userSubsToBlock.filter(item => getActionAvailability(item)[flagName])
        if (eligibleRows.length === 0) return { checked: false, indeterminate: false }

        const checkedCount = eligibleRows.filter(item => rowFlags[item._row_id] && rowFlags[item._row_id][flagName])
            .length

        return {
            checked: checkedCount === eligibleRows.length,
            indeterminate: checkedCount > 0 && checkedCount < eligibleRows.length
        }
    }

    renderColumnHeader(title: string, flagName: ActionFlag) {
        const state = this.getColumnHeaderState(flagName)
        return (
            <div style={{ textAlign: 'center', lineHeight: 1.2 }}>
                <Checkbox
                    checked={state.checked}
                    indeterminate={state.indeterminate}
                    onChange={e => this.handleColumnToggle(flagName, e.target.checked)}
                />
                <div style={{ fontSize: 12, marginTop: 4, whiteSpace: 'nowrap' }}>{title}</div>
            </div>
        )
    }

    closeModal = () => {
        this.uploadSeq += 1
        this.cleanupProgress()
        this.setState(getInitialState())
        this.props.onClose()
    }

    // ── render sections ─────────────────────────────────────
    renderTerminal() {
        const { logs } = this.state
        if (!logs.length) return null

        return (
            <div
                style={{
                    background: '#1a1a2e',
                    borderRadius: 4,
                    padding: '10px 14px',
                    maxHeight: 200,
                    overflowY: 'auto',
                    marginTop: 16,
                    fontFamily: '"Courier New", monospace',
                    fontSize: 12,
                    lineHeight: '22px',
                    direction: 'rtl',
                    textAlign: 'right'
                }}
            >
                {logs.map((log, i) => (
                    <div key={i} style={{ color: LOG_COLORS[log.type] }}>
                        <span style={{ color: '#595959' }}>[{log.time}]</span>
                        {` ${LOG_PREFIX[log.type]}  ${log.text}`}
                    </div>
                ))}
                <div ref={this.logEndRef} />
            </div>
        )
    }

    renderUploadStage() {
        const { csvUploadDate, uploadStatus, data, batchOutcome } = this.state

        return (
            <>
                <Upload.Dragger accept=".csv" beforeUpload={this.handleUpload} showUploadList={false}>
                    <p className="ant-upload-drag-icon">
                        <Icon type={uploadStatus.icon} style={{ color: uploadStatus.color }} />
                    </p>
                    <p className="ant-upload-text" style={{ color: uploadStatus.color }}>
                        {uploadStatus.title}
                    </p>
                </Upload.Dragger>

                <DatePicker
                    value={csvUploadDate}
                    onChange={date => this.setState({ csvUploadDate: date, batchOutcome: null })}
                    format="DD/MM/YYYY"
                    style={{ width: '100%', marginTop: 16, height: 34 }}
                    disabledDate={current => current && current > moment().endOf('day')}
                    placeholder="בחר תאריך"
                />

                {batchOutcome === 'done' && (
                    <Alert
                        type="success"
                        showIcon
                        style={{ marginTop: 16 }}
                        message="הקובץ עובד בהצלחה (למרות שגיאת התקשורת). אין צורך לנסות שוב."
                    />
                )}
                {batchOutcome === 'in_progress' && (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginTop: 16 }}
                        message="העיבוד עדיין מתבצע. בדוק שוב מאוחר יותר ואל תנסה שוב כעת."
                    />
                )}

                {/* When the batch already committed (done), hide the start button and offer closing the
                    modal — a retry would only 409. While in_progress, keep the button but disable it. */}
                {data.length > 0 &&
                    (batchOutcome === 'done' ? (
                        <Button
                            icon="close"
                            style={{ marginTop: 16, width: '100%', direction: 'ltr' }}
                            onClick={this.closeModal}
                        >
                            סגור
                        </Button>
                    ) : (
                        <Button
                            type="primary"
                            icon="play-circle"
                            disabled={batchOutcome === 'in_progress'}
                            style={{ marginTop: 16, width: '100%', direction: 'ltr' }}
                            onClick={this.handleStartProcessing}
                        >
                            {`התחל עיבוד (${data.length} שורות)`}
                        </Button>
                    ))}
            </>
        )
    }

    renderProcessingStage() {
        const { progress } = this.state

        if (progress && progress.stage === 'fetch') {
            return (
                <div style={{ textAlign: 'center', padding: '24px 0 8px' }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 12, color: '#8c8c8c' }}>מאתר חיובים בטרנזילה...</div>
                </div>
            )
        }

        if (progress && progress.total > 0) {
            const percent = Math.round((progress.processed / progress.total) * 100)
            return (
                <div style={{ textAlign: 'center', padding: '24px 0 8px' }}>
                    <Progress type="circle" percent={percent} width={100} />
                    <div style={{ marginTop: 12, color: '#8c8c8c' }}>
                        {`מעבד שורה ${progress.processed} מתוך ${progress.total}`}
                    </div>
                </div>
            )
        }

        // fallback: no socket / no progress yet → spinner
        return (
            <div style={{ textAlign: 'center', padding: '24px 0 8px' }}>
                <Spin size="large" />
                <div style={{ marginTop: 12, color: '#8c8c8c' }}>מעבד נתונים...</div>
            </div>
        )
    }

    renderResultsStage() {
        const { results, userSubsToBlock, rowFlags, loading, applySkipped } = this.state
        if (!results) return null

        const BLOCK_DB_TYPES: RowType[] = ['full', 'has_cancellation']
        const BLOCK_TZ_TYPES: RowType[] = [
            'full',
            'has_cancellation',
            'blocked_sub',
            'no_sub',
            'no_user',
            'sub_mismatch',
            'other_status'
        ]
        // sub_mismatch is counted twice on purpose (like other_status): the freeze is available,
        // and the mismatched subscription still needs investigation.
        const NO_ACTION_TYPES: RowType[] = ['not_in_tranzila', 'other_status', 'sub_mismatch']

        const totalCount = userSubsToBlock.length
        const blockDbCount = userSubsToBlock.filter(r => BLOCK_DB_TYPES.indexOf(r.row_type) !== -1).length
        const blockTzCount = userSubsToBlock.filter(r => BLOCK_TZ_TYPES.indexOf(r.row_type) !== -1).length
        const missingCount = userSubsToBlock.filter(r => NO_ACTION_TYPES.indexOf(r.row_type) !== -1).length
        const hasRows = userSubsToBlock.length > 0
        // Is anything in this batch inherently actionable? Distinct from handleApplyChanges'
        // hasAnyAction, which asks what the operator has SELECTED. Reuses the availability matrix.
        const hasActionableRow = userSubsToBlock.some(item => {
            const avail = getActionAvailability(item)
            return avail.tranzila || avail.block || avail.cancellation
        })
        let emptyResultsMessage = ''
        let emptyResultsType: 'info' | 'warning' = 'info'

        if (!hasRows) {
            const decision = emptyResultsDecision(!!this.props.applyMode, applySkipped)
            emptyResultsMessage = decision.message
            emptyResultsType = decision.type
        }

        const renderUserName = (_text: any, record: UserSubToBlock) => {
            if (record.row_type === 'not_in_tranzila') {
                return <span style={{ color: '#f5222d' }}>לא נמצא חיוב פעיל</span>
            }
            if (record.row_type === 'no_sub' || record.row_type === 'other_status') {
                return <span style={{ color: '#f5222d' }}>לא נמצא מנוי תואם</span>
            }
            if (record.row_type === 'no_user') {
                return <span style={{ color: '#f5222d' }}>לא נמצא משתמש</span>
            }
            // A real subscription WAS found — keep the name visible so the operator can investigate,
            // and flag that it belongs to a different STO.
            if (record.row_type === 'sub_mismatch') {
                return (
                    <span>
                        {record.user_name}
                        <span style={{ color: '#f5222d' }}> — משויך לחיוב אחר</span>
                    </span>
                )
            }
            return <span>{record.user_name}</span>
        }

        const renderUserId = (_text: any, record: UserSubToBlock) => {
            if (record.user_id === null) return <span>—</span>
            return (
                <a target="_blank" href={`/clients/${record.user_id}`} rel="noopener noreferrer">
                    {record.user_id}
                </a>
            )
        }

        const renderNullable = (val: string | number | null) => (val !== null ? val : <span>—</span>)

        const renderActionCheckbox = (flagName: ActionFlag, record: UserSubToBlock) => {
            const flags = rowFlags[record._row_id] || ALL_FALSE_FLAGS
            const available = getActionAvailability(record)[flagName]

            if (available) {
                const checkbox = (
                    <Checkbox
                        checked={flags[flagName]}
                        onChange={e => this.handleActionToggle(record._row_id, flagName, e.target.checked)}
                    />
                )
                // An open cancellation request is INFO only — show it next to the (enabled, default-checked)
                // cancellation checkbox without disabling it; backend handles it as close+create.
                if (flagName === 'cancellation' && record.has_cancellation_request) {
                    return (
                        <span>
                            {checkbox}
                            <Tooltip title="בקשת ביטול כבר קיימת">
                                <Icon
                                    type="info-circle"
                                    style={{ color: '#bfbfbf', cursor: 'help', fontSize: 16, marginRight: 6 }}
                                />
                            </Tooltip>
                        </span>
                    )
                }
                return checkbox
            }

            // Action is unavailable: show an explanatory icon when possible, otherwise render a dash.
            const tooltipText = getActionTooltip(record, flagName)
            if (tooltipText) {
                return (
                    <Tooltip title={tooltipText}>
                        <Icon type="info-circle" style={{ color: '#bfbfbf', cursor: 'help', fontSize: 16 }} />
                    </Tooltip>
                )
            }

            return <span style={{ color: '#bfbfbf' }}>—</span>
        }

        const renderSelectCheckbox = (_t: any, record: UserSubToBlock) => {
            const avail = getActionAvailability(record)
            const selectEnabled = avail.tranzila || avail.block || avail.cancellation
            if (!selectEnabled) {
                return <span style={{ color: '#bfbfbf' }}>—</span>
            }
            const flags = rowFlags[record._row_id] || ALL_FALSE_FLAGS
            return (
                <Checkbox
                    checked={flags.select}
                    onChange={e => this.handleSelectToggle(record._row_id, e.target.checked)}
                />
            )
        }

        const center: 'center' = 'center'

        const columns = [
            {
                title: 'בחר',
                key: 'select',
                width: 60,
                align: center,
                render: renderSelectCheckbox
            },
            {
                title: 'מזהה מנוי',
                dataIndex: 'user_sub_id',
                key: 'user_sub_id',
                width: 100,
                align: center,
                render: renderNullable
            },
            { title: 'שם משתמש', key: 'user_name', align: center, render: renderUserName },
            { title: 'מזהה משתמש', key: 'user_id', width: 110, align: center, render: renderUserId },
            {
                title: 'מזהה חיוב',
                dataIndex: 'sto_id',
                key: 'sto_id',
                width: 100,
                align: center,
                render: renderNullable
            },
            { title: 'מסוף', dataIndex: 'terminal_name', key: 'terminal_name', width: 110, align: center },
            {
                title: '4 ספרות',
                dataIndex: 'last_4_digits',
                key: 'last_4_digits',
                width: 80,
                align: center,
                render: (val: string | null, record: UserSubToBlock) => (
                    <Tooltip title={record.existing_token}>
                        <span style={{ cursor: 'help' }}>{val !== null ? val : '—'}</span>
                    </Tooltip>
                )
            },
            {
                title: this.renderColumnHeader('ביטול בטרנזילה', 'tranzila'),
                key: 'tranzila',
                width: 110,
                align: center,
                render: (_t: any, record: UserSubToBlock) => renderActionCheckbox('tranzila', record)
            },
            {
                title: this.renderColumnHeader('חסימת מנוי', 'block'),
                key: 'block',
                width: 100,
                align: center,
                render: (_t: any, record: UserSubToBlock) => renderActionCheckbox('block', record)
            },
            {
                title: this.renderColumnHeader('בקשת ביטול', 'cancellation'),
                key: 'cancellation',
                width: 100,
                align: center,
                render: (_t: any, record: UserSubToBlock) => renderActionCheckbox('cancellation', record)
            }
        ]

        const applyButton = (
            <Button
                type="primary"
                icon="swap"
                loading={loading}
                disabled={!hasActionableRow}
                style={{ width: '100%', direction: 'ltr' }}
                onClick={this.handleApplyChanges}
            >
                בצע פעולות
            </Button>
        )

        const { rerunAbort, applyAbort } = this.state
        // An aborted rerun never reached every record, so neither the green success nor a bare
        // "no records" reading is true — the banner replaces both.
        const rerunAbortCounts =
            rerunAbort && rerunAbort.processed !== null && rerunAbort.total !== null
                ? `נבדקו ${rerunAbort.processed} מתוך ${rerunAbort.total} רשומות`
                : ''

        return (
            <>
                {rerunAbort ? (
                    <Alert
                        message="הריצה הופסקה — טרנזילה אינה זמינה. חלק מהרשומות לא נבדקו — יש להריץ שוב מאוחר יותר"
                        description={rerunAbortCounts ? <span>{rerunAbortCounts}</span> : undefined}
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16, direction: 'rtl' }}
                    />
                ) : hasRows ? (
                    // The records were created and are visible in the listing, but with nothing
                    // selectable this is not a success the operator can act on.
                    hasActionableRow ? (
                        <Alert message="העיבוד הושלם בהצלחה" type="success" showIcon style={{ marginBottom: 16 }} />
                    ) : (
                        <Alert
                            message="הקובץ עובד — לא נמצאו פעולות לביצוע. ניתן לבדוק את הרשומות ברשימה"
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16, direction: 'rtl' }}
                        />
                    )
                ) : (
                    <Alert
                        message={emptyResultsMessage}
                        type={emptyResultsType}
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}

                {applyAbort && (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 12, direction: 'rtl' }}
                        message={`הריצה נעצרה לאחר ${applyAbort.processed} מתוך ${
                            applyAbort.total
                        } רשומות — טרנזילה אינה זמינה. אין לשלוח שוב את אותן רשומות`}
                        description={
                            applyAbort.four_digits ? (
                                <span>{`הריצה נעצרה ברשומה ${applyAbort.four_digits}`}</span>
                            ) : (
                                undefined
                            )
                        }
                    />
                )}

                {this.state.applySkipped.length > 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 12, direction: 'rtl' }}
                        message={`${this.state.applySkipped.length} רשומות לא עודכנו`}
                        description={
                            <span>
                                {this.state.applySkipped
                                    .map(s => {
                                        const reasonLabel =
                                            (s.reason && SKIPPED_REASON_LABELS[s.reason]) || 'סיבה לא ידועה'
                                        return `${s.four_digits || s.id} — ${reasonLabel}`
                                    })
                                    .join(', ')}
                            </span>
                        }
                    />
                )}

                {hasRows && (
                    <Row gutter={16} style={{ textAlign: 'center', marginBottom: 16 }}>
                        <Col span={6}>
                            <Statistic title="סה״כ שורות" value={totalCount} />
                        </Col>
                        <Col span={6}>
                            <Statistic
                                title="מנויים לחסימה במערכת"
                                value={blockDbCount}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Col>
                        <Col span={6}>
                            <Statistic
                                title="מנויים לחסימה בטרנזילה"
                                value={blockTzCount}
                                valueStyle={{ color: '#13c2c2' }}
                            />
                        </Col>
                        <Col span={6}>
                            <Statistic title="נתונים חסרים" value={missingCount} valueStyle={{ color: '#faad14' }} />
                        </Col>
                    </Row>
                )}

                {userSubsToBlock.length > 0 && (
                    <>
                        <Table
                            className="csv-uploader-table"
                            rowKey="_row_id"
                            dataSource={userSubsToBlock}
                            columns={columns}
                            pagination={false}
                            size="small"
                            scroll={{ y: 300 }}
                            style={{ marginBottom: 16, direction: 'rtl' }}
                            rowClassName={(record: UserSubToBlock) => {
                                if (record.row_type === 'not_in_tranzila') return 'csv-uploader-row-red'
                                if (
                                    record.row_type === 'no_sub' ||
                                    record.row_type === 'no_user' ||
                                    record.row_type === 'sub_mismatch' ||
                                    record.row_type === 'other_status' ||
                                    record.row_type === 'blocked_sub'
                                )
                                    return 'csv-uploader-row-grey'
                                return ''
                            }}
                        />

                        {/* Tooltip wraps a block span: a disabled control fires no mouse events, and
                            the button needs a block-level parent to keep its full width. */}
                        {hasActionableRow ? (
                            applyButton
                        ) : (
                            <Tooltip title="אין פעולות זמינות עבור הרשומות בקובץ">
                                <span style={{ display: 'block' }}>{applyButton}</span>
                            </Tooltip>
                        )}
                    </>
                )}
            </>
        )
    }

    renderDateSelectStage() {
        const dates = this.props.applyDates || []
        return (
            <div style={{ padding: 16, direction: 'rtl' }}>
                <div style={{ marginBottom: 12, fontWeight: 500 }}>בחר תאריך להרצת שלב ביצוע:</div>
                {dates.map(d => (
                    <div key={d.date} style={{ marginBottom: 8 }}>
                        <Button onClick={() => this.loadApplyRows(d.date)}>
                            {d.date} ({d.count})
                        </Button>
                    </div>
                ))}
            </div>
        )
    }

    renderDoneStage() {
        const { doneSummary, applyAllSkipped, auditFailed } = this.state

        // Nothing ran — all-zero counters beside this would only muddle it.
        if (applyAllSkipped) {
            return (
                <Alert
                    message="הרשומות כבר עודכנו על ידי משתמש אחר — לא בוצעה פעולה"
                    type="info"
                    showIcon
                    style={{ direction: 'rtl' }}
                />
            )
        }

        // The actions ran but were never recorded: the customer was affected and the system has no
        // trace of it. This outranks the failure counters below.
        const hasAuditFailures = auditFailed.length > 0
        const auditAlert = hasAuditFailures ? (
            <Alert
                type="error"
                showIcon
                style={{ marginBottom: 12, direction: 'rtl' }}
                message={`${auditFailed.length} רשומות: הפעולות בוצעו אך התיעוד נכשל — יש לפנות לתמיכה ולא לנסות שוב`}
                description={<span>{`מזהי רשומות: ${auditFailed.join(', ')}`}</span>}
            />
        ) : null

        if (!doneSummary) {
            return (
                <>
                    {auditAlert}
                    <Alert message="הפעולה הושלמה" type={hasAuditFailures ? 'error' : 'success'} showIcon />
                </>
            )
        }

        const line = (label: string, c: ActionCounters) => `${label}: ${c.success} הצליחו, ${c.failed} נכשלו`
        const anyFailed =
            doneSummary.tranzila.failed + doneSummary.block.failed + doneSummary.cancellation.failed > 0
        return (
            <>
                {auditAlert}
                <Alert
                    message="הפעולה הושלמה"
                    description={
                        <div>
                            <div>{line('ביטול בטרנזילה', doneSummary.tranzila)}</div>
                            <div>{line('חסימת מנוי', doneSummary.block)}</div>
                            <div>{line('בקשת ביטול', doneSummary.cancellation)}</div>
                        </div>
                    }
                    type={hasAuditFailures ? 'error' : anyFailed ? 'warning' : 'success'}
                    showIcon
                />
            </>
        )
    }

    render() {
        const { visible } = this.props
        const { stage, userSubsToBlock } = this.state

        const isProcessing = stage === 'processing'

        return (
            <Modal
                visible={visible}
                onCancel={this.closeModal}
                title={this.props.applyMode ? 'הרצת שלב ביצוע' : 'קובץ מוחלפים'}
                footer={null}
                closable={!isProcessing}
                keyboard={!isProcessing}
                maskClosable={!isProcessing}
                width={userSubsToBlock.length > 0 ? 1300 : 600}
                destroyOnClose
            >
                <style>{`
                    .csv-uploader-row-grey {
                        background-color: #fafafa;
                    }
                    .csv-uploader-row-grey td {
                        color: #bfbfbf;
                    }
                    .csv-uploader-row-red {
                        background-color: #fff1f0;
                    }
                    .csv-uploader-row-red td {
                        color: #f5222d;
                    }
                    .csv-uploader-table .ant-table-thead > tr > th,
                    .csv-uploader-table .ant-table-tbody > tr > td {
                        text-align: center;
                        vertical-align: middle;
                        white-space: nowrap;
                        padding: 8px;
                    }
                `}</style>
                <div style={{ padding: '12px 8px' }}>
                    {stage === 'upload' && this.renderUploadStage()}
                    {stage === 'date_select' && this.renderDateSelectStage()}
                    {stage === 'processing' && this.renderProcessingStage()}
                    {stage === 'results' && this.renderResultsStage()}
                    {stage === 'done' && this.renderDoneStage()}
                    {this.renderTerminal()}
                </div>
            </Modal>
        )
    }
}

export default CsvUploader
